import { Link } from '@tanstack/react-router'
import { useAuth } from '../contexts/AuthContext'
import { usePageTitle } from '../hooks/usePageTitle'
import { useRoundsFeed } from '../hooks/useRoundsFeed'
import type { FeedRound } from '../hooks/useRoundsFeed'
import { formatPlayedOn } from '../lib/golfFormat'
import {
  AreaSectionHead,
  BookGate,
  ClubPage,
  Container,
  EmptyPlate,
  Eyebrow,
  PageHead,
} from '../components/golf'

/**
 * The Lounge (route map §4 `/clubhouse/lounge`) — the steward's note, the
 * comings and goings, and what's on.
 *
 * The steward's note is kept almost verbatim: it is the club talking, and the
 * design card already has it admitting the grounds are not quite open.
 *
 * COMINGS & GOINGS IS REAL. It was an unconditional "Nothing to report — yet."
 * plate; it is now `GET /golf/rounds/feed` — the same posted rounds the
 * clubhouse home tables — written as prose, newest first, capped at eight. The
 * plate survives, conditionally, for a club that genuinely has posted nothing.
 *
 * The card's own six-item feed is still refused and always will be: every line
 * of it credits an invented member with a score or a seat. What ships here is
 * the club's own rounds and nothing else — no seats claimed, no records taken,
 * because neither has an endpoint. "Seven places remain" on Twilight Nine stays
 * out too: it is a scarcity claim about an evening nobody can book.
 *
 * The two club nights stay as static announcements, both awaiting a date.
 *
 * Neither names a VENUE. Twilight Nine used to be held "at The Quicknine", one
 * of the invented courses the Grounds retired; the survival was put back to the
 * maintainer and dropped by his answer (`club-web-decisions.md` §10,
 * 2026-08-22). The club has no venue to name until it books one, so the line
 * says when and how long, and nothing about where.
 */

const CLUB_NIGHTS = [
  {
    when: 'Friday · Dusk · Nine holes',
    title: 'Twilight Nine',
    body: 'Nine holes as the light goes down, in company. A standing Friday fixture once the grounds open — the first tee at eight, sharp.',
    note: 'Date to be posted',
  },
  {
    when: 'When the charter closes',
    title: "The Founders' Toast",
    body: 'The first hundred raise a glass as the charter is sealed. Dress: anything you can leave in.',
    note: 'Date to be posted',
  },
]

/** How many cards the lounge prints before it sends you to the full book. */
const ENTRIES_SHOWN = 8

interface Entry {
  id: string
  roundId: string
  name: string
  guest: boolean
  gross: number | null
  course: string
  playedOn: string | null
}

/**
 * One line per PLAYER's card, in the feed's own order — which is the club's
 * order, newest posting first. Deliberately NOT re-sorted here: the feed
 * decides what "lately" means, and a second opinion about it on this page
 * could only ever disagree with the clubhouse home's table.
 */
function entriesFrom(rounds: FeedRound[]): Entry[] {
  return rounds
    .flatMap((round) =>
      round.players.map((player, index) => ({
        id: `${round.id}-${index}`,
        roundId: round.id,
        name: player.display_name,
        guest: player.guest,
        gross: player.total_strokes,
        course: round.course_name,
        playedOn: round.played_on,
      })),
    )
    .slice(0, ENTRIES_SHOWN)
}

export function TheLoungeScreen() {
  usePageTitle('The Lounge')
  const { isAuthenticated } = useAuth()
  const { rounds, isLoading, error, membershipRequired } = useRoundsFeed(isAuthenticated)
  const entries = entriesFrom(rounds)

  return (
    <ClubPage chrome="clubhouse">
      <PageHead
        eyebrow="The Lounge"
        title="Members"
        emphasis="only."
        lede="Where the club convenes between rounds — news of the grounds, evenings on the book, and company in good standing."
      />

      {/* ── The steward's note ── */}
      <section className="pb-16">
        <Container>
          <div className="on-pine relative overflow-hidden rounded-club-lg bg-pine px-8 py-10 text-paper sm:px-11">
            <span
              aria-hidden="true"
              className="absolute -right-11 -top-11 size-[210px] rounded-full border border-gilt/28"
            />
            <span
              aria-hidden="true"
              className="absolute -right-3.5 -top-3.5 size-[146px] rounded-full border border-gilt/18"
            />
            <div className="relative z-10">
              <Eyebrow tone="gilt">A note from the Steward</Eyebrow>
              <p className="mt-3.5 max-w-[56ch] font-display text-[clamp(1.25rem,2.3vw,1.5625rem)] leading-[1.42] text-paper">
                The grounds are seeded and very nearly in season — the first tee times will be
                posted here, and your hour will arrive by post. Until then the Lounge keeps its
                usual hours. Which is to say: all of them.
              </p>
              <p className="mt-5 font-display text-[13px] italic text-sage">
                — The Steward, on behalf of the Committee
              </p>
            </div>
          </div>
        </Container>
      </section>

      <section className="pb-16">
        <Container>
          <div className="grid items-start gap-8.5 lg:grid-cols-[1.25fr_0.75fr]">
            {/* ── Comings & goings ── */}
            <div>
              <AreaSectionHead eyebrow="Lately" title="Comings &amp; goings." />
              <div className="rounded-club-lg border border-ink/12 bg-parchment p-6">
                <BookGate
                  isAuthenticated={isAuthenticated}
                  membershipRequired={membershipRequired}
                  error={error}
                >
                  {isLoading && <p className="text-[13.5px] text-ink-soft">Opening the book…</p>}

                  {!isLoading && entries.length === 0 && (
                    <EmptyPlate
                      title="Nothing to report — yet."
                      description="Rounds carded by the membership appear here. The club is very new; the first entry is still to come."
                    />
                  )}

                  {entries.length > 0 && (
                    <>
                      <ul className="flex flex-col">
                        {entries.map((entry) => (
                          <li
                            key={entry.id}
                            className="flex flex-wrap items-baseline gap-x-2 gap-y-1 border-b border-brass/25 py-3 last:border-b-0"
                          >
                            {/* The whole line is the link: a member reading a
                                sentence about a round should be able to press
                                the sentence. The route lands with the round
                                detail screen. */}
                            <Link
                              to={`/clubhouse/rounds/${entry.roundId}`}
                              className="rounded-club-sm text-[13.5px] text-ink"
                            >
                              <b className="font-semibold text-pine">{entry.name}</b>
                              {entry.gross === null
                                ? ` went out at ${entry.course}`
                                : ` carded ${entry.gross} at ${entry.course}`}
                              <span className="text-ink-soft">
                                {' — '}
                                {formatPlayedOn(entry.playedOn)}
                              </span>
                            </Link>
                            {entry.guest && (
                              <span className="whitespace-nowrap rounded-club-sm border border-ink-soft/35 px-1.5 py-0.5 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
                                Guest
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                      <p className="pt-4">
                        <Link
                          to="/clubhouse"
                          className="rounded-club-sm text-[13.5px] font-semibold text-fairway"
                        >
                          The full book →
                        </Link>
                      </p>
                    </>
                  )}
                </BookGate>
              </div>
            </div>

            {/* ── Club nights ── */}
            <div>
              <AreaSectionHead eyebrow="What's on" title="Club nights." />
              <ul className="flex flex-col gap-5">
                {CLUB_NIGHTS.map((night) => (
                  <li
                    key={night.title}
                    className="flex min-h-[210px] flex-col rounded-club-lg border border-ink/12 bg-parchment p-7"
                  >
                    <p className="font-display text-[13px] text-brass-deep">{night.when}</p>
                    <h3 className="mb-2.5 mt-1.5 font-display text-[22px] font-medium text-pine">
                      {night.title}
                    </h3>
                    <p className="flex-1 text-sm text-ink-soft">{night.body}</p>
                    <p className="mt-4 border-t border-brass/30 pt-4 text-[10.5px] font-semibold uppercase tracking-[0.12em] text-ink-soft">
                      {night.note}
                    </p>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Container>
      </section>
    </ClubPage>
  )
}
